/* eslint-disable no-console */
import {FC, useState, useCallback, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {v4 as uuidv4} from "uuid";
import {useMutation, useSubscription} from "@apollo/client";
import {
  ArrowUturnLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from "@interstate/components/Icons";
import {Button} from "@interstate/components/Button";
import {Stepper, StepperLabels} from "@interstate/components/Stepper";
import {Endpoints} from "../../../../runtime";
import {useFeedback} from "../../../../feedback";
import {CreateNewEntityFormData} from "../CreateNewEntityForm";
import {CreateNewEntityProvider, useCreateNewEntity} from "./context";
import {
  OnCreateOperationalEntity,
  OnCreateOperationalEntityData,
  OnCreateOperationalEntityVariables
} from "../../../backend";
import {
  CreateSimpleOperationalHierarchy,
  CreateSimpleOperationalHierarchyData,
  CreateSimpleOperationalHierarchyVariables
} from "../../create-hierarchy/backend";
import {
  CreateNewEntityStep,
  createNewEntityStepKey,
  CreateNewEntityStepError
} from "./view-model";
import {
  CreateNewEntityFormStep,
  CreateNewEntityReviewStep,
  CreateNewEntityResultsStep
} from "./steps";
import "./CreateNewEntityStepper.scss";

const labelKey = (step: CreateNewEntityStep): string => {
  return `create-new-entity.stepper.step-labels.${createNewEntityStepKey(step)}`;
};

export interface CreateNewEntityStepperProps {
  onSuccess: (entityId: string) => void;
  onCancel: () => void;
}

/**
 * CreateNewEntityStepper provides a 3-step workflow for creating test entities:
 * 1. CREATE_FORM (CreateNewEntityFormStep) - Form input
 * 2. REVIEW_ENTITY (CreateNewEntityReviewStep) - Entity preview
 * 3. RESULTS (CreateNewEntityResultsStep) - Creation results
 *
 * Follows the same pattern as HierarchyBuilder but adapted for single entity creation.
 */
export const CreateNewEntityStepper: FC<CreateNewEntityStepperProps> = ({
  onSuccess,
  onCancel
}) => {
  return (
    <CreateNewEntityProvider>
      <CreateNewEntityStepperInternal
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </CreateNewEntityProvider>
  );
};

/**
 * Internal stepper component that uses the TestEntity context
 */
const CreateNewEntityStepperInternal: FC<CreateNewEntityStepperProps> = ({
  onSuccess,
  onCancel
}) => {
  const {t} = useTranslation();
  const [, setFeedback] = useFeedback();
  const controller = useCreateNewEntity();

  // Local stepper navigation state
  const [activeStep, setActiveStep] = useState<CreateNewEntityStep>(
    CreateNewEntityStep.CREATE_FORM
  );
  const [transactionId] = useState<string>(() => uuidv4());
  const [working, setWorking] = useState<boolean>(false);
  const [completed, setCompleted] = useState<boolean>(false);
  const [stepError, setStepError] = useState<CreateNewEntityStepError>();

  // Handle step transition from form to review (when form is successfully validated)
  useEffect(() => {
    if (
      controller.isSubmitSuccessful &&
      activeStep === CreateNewEntityStep.CREATE_FORM
    ) {
      setStepError(undefined);
      setActiveStep(CreateNewEntityStep.REVIEW_ENTITY);
    }
  }, [controller.isSubmitSuccessful, activeStep, controller]);

  // Handle successful entity creation - mark stepper as completed
  useEffect(() => {
    if (
      controller.entityStatus === "created" &&
      activeStep === CreateNewEntityStep.RESULTS
    ) {
      setCompleted(true);
    } else if (controller.entityStatus === "failed") {
      setCompleted(false);
    }
  }, [controller.entityStatus, activeStep]);

  // Results step action handlers
  const handleCreateAnother = useCallback(() => {
    // Reset the stepper to start over
    controller.clearSubmissionState();
    setActiveStep(CreateNewEntityStep.CREATE_FORM);
    setCompleted(false);
    setStepError(undefined);
  }, [controller]);

  const handleViewEntity = useCallback(
    (entityId: string) => {
      // Navigate to entity view - this would be implemented based on routing structure
      onSuccess(entityId);
    },
    [onSuccess]
  );

  const handleReturnToDashboard = useCallback(() => {
    // Return to the dashboard or parent page
    onCancel();
  }, [onCancel]);

  // Transform form data to OrganizationInformation format for review step
  const transformFormDataForReview = useCallback(
    (formData: CreateNewEntityFormData) => {
      return {
        legalName: formData.legalName || undefined,
        dbaName: formData.dbaName,
        status: formData.status,
        address: {
          streetAddressLine1: formData.streetAddressLine1 || undefined,
          streetAddressLine2: formData.streetAddressLine2 || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          country: formData.country || undefined,
          zip: formData.zip || undefined
        },
        telecomNumbers: formData.telecomNumbers
          ? formData.telecomNumbers
              .split(",")
              .map(num => num.trim())
              .filter(num => num.length > 0)
          : []
      };
    },
    []
  );

  // Placeholder mutation - will be implemented in Step 6
  const [,/* createSimpleOperationalHierarchy */] = useMutation<
    CreateSimpleOperationalHierarchyData,
    CreateSimpleOperationalHierarchyVariables
  >(CreateSimpleOperationalHierarchy, {
    context: {
      endpoint: Endpoints.APPSYNC
    },
    fetchPolicy: "network-only"
  });

  useSubscription<
    OnCreateOperationalEntityData,
    OnCreateOperationalEntityVariables
  >(OnCreateOperationalEntity, {
    variables: {transactionId},
    context: {
      endpoint: Endpoints.APPSYNC
    },
    onData: ({data: {data}}) => {
      if (!data?.result) {
        return;
      }
    },
    onError: error => {
      if (error.networkError) {
        console.error("Network error details:", error.networkError);
      }
      if (error.graphQLErrors?.length) {
        console.error("GraphQL errors:", error.graphQLErrors);
      }
    }
  });

  const createNewEntity = async () => {
    setStepError(undefined);
    setFeedback(undefined);
    setWorking(true);

    try {
      // Navigate to results step first, then create the entity
      setActiveStep(CreateNewEntityStep.RESULTS);
      // Use the context provider's entity creation handler
      await controller.onCreateEntity();
    } catch (error) {
      setStepError({
        step: CreateNewEntityStep.REVIEW_ENTITY,
        error
      });
    } finally {
      setWorking(false);
    }
  };

  const stepperLabel = (step: CreateNewEntityStep): StepperLabels => {
    const defaults: StepperLabels = {
      slotProps: {
        button: {
          dataAttr: {
            "data-testid": `${createNewEntityStepKey(step)}-stepper-button`
          }
        }
      }
    };
    // Check if there is an error for the current step
    if (stepError && stepError.step === step) {
      return {
        ...defaults,
        label: t("create-new-entity.stepper.step-labels.error"),
        error: true
      };
    }
    // This forces the completed state when there are results
    if (activeStep === CreateNewEntityStep.RESULTS) {
      return {
        ...defaults,
        label: t(labelKey(step)),
        completed
      };
    }
    return {
      ...defaults,
      label: t(labelKey(step))
    };
  };

  const renderActiveStep = () => {
    switch (activeStep) {
      case CreateNewEntityStep.CREATE_FORM:
        return <CreateNewEntityFormStep onCancel={onCancel} />;
      case CreateNewEntityStep.REVIEW_ENTITY:
        // Use form data from context for review
        if (!controller.formData.dbaName) {
          // Fallback to form step if no data (shouldn't happen in normal flow)
          setActiveStep(CreateNewEntityStep.CREATE_FORM);
          return null;
        }
        return (
          <CreateNewEntityReviewStep
            transactionId={`new-entity-${transactionId.split("-")[0]}`}
            organizationInformation={transformFormDataForReview(
              controller.formData
            )}
            oems={[]}
          />
        );
      case CreateNewEntityStep.RESULTS:
        return (
          <CreateNewEntityResultsStep
            onCreateAnother={handleCreateAnother}
            onViewEntity={handleViewEntity}
            onReturnToDashboard={handleReturnToDashboard}
          />
        );
    }
  };

  return (
    <section className={"create-new-entity-stepper"}>
      <Stepper
        data-testid={"create-new-entity-stepper"}
        activeStep={activeStep}
        labels={[
          stepperLabel(CreateNewEntityStep.CREATE_FORM),
          stepperLabel(CreateNewEntityStep.REVIEW_ENTITY),
          stepperLabel(CreateNewEntityStep.RESULTS)
        ]}
      />
      {renderActiveStep()}
      <div data-testid={"stepper-controls"} className={"stepper-controls"}>
        <div
          data-testid={"stepper-controls-left"}
          className={"stepper-controls-left"}>
          {activeStep === CreateNewEntityStep.REVIEW_ENTITY && (
            <Button
              id={"stepper-navigation-previous-button"}
              data-testid={"stepper-navigation-previous-button"}
              buttonStyle={"secondary"}
              className={"stepper-navigation-button"}
              startIcon={<ChevronLeftIcon />}
              onClick={() => {
                setStepError(undefined);
                setActiveStep(CreateNewEntityStep.CREATE_FORM);
              }}>
              {t(labelKey(CreateNewEntityStep.CREATE_FORM))}
            </Button>
          )}
          {activeStep === CreateNewEntityStep.RESULTS && stepError && (
            <Button
              id={"stepper-navigation-previous-button"}
              data-testid={"stepper-navigation-previous-button"}
              buttonStyle={"secondary"}
              className={"stepper-navigation-button"}
              startIcon={<ChevronLeftIcon />}
              onClick={() => {
                setStepError(undefined);
                setActiveStep(CreateNewEntityStep.REVIEW_ENTITY);
              }}>
              {t(labelKey(CreateNewEntityStep.REVIEW_ENTITY))}
            </Button>
          )}
        </div>
        <div
          data-testid={"stepper-controls-right"}
          className={"stepper-controls-right"}>
          {(activeStep !== CreateNewEntityStep.RESULTS ||
            (activeStep === CreateNewEntityStep.RESULTS && stepError)) && (
            <Button
              id={"step-action-cancel-button"}
              data-testid={"step-action-cancel-button"}
              className={"step-action-button"}
              buttonStyle={"tertiary"}
              disabled={working}
              onClick={onCancel}>
              {t("create-new-entity.stepper.controls.actions.cancel")}
            </Button>
          )}
          {activeStep === CreateNewEntityStep.CREATE_FORM && (
            <Button
              id={"review-plan-button"}
              data-testid={"review-plan-button"}
              className={"stepper-navigation-button"}
              buttonStyle={"primary"}
              endIcon={<ChevronRightIcon />}
              disabled={!controller.isFormValid || working}
              onClick={() => {
                // Navigate to review step without triggering context submission
                setActiveStep(CreateNewEntityStep.REVIEW_ENTITY);
              }}>
              {t("create-new-entity.review-plan", "Review Plan")}
            </Button>
          )}
          {activeStep === CreateNewEntityStep.RESULTS &&
            !stepError &&
            !working && (
              <Button
                id={"back-to-create-new-entities-button"}
                data-testid={"back-to-create-new-entities-button"}
                className={"back-to-create-new-entities-button"}
                buttonStyle={"tertiary"}
                startIcon={<ArrowUturnLeftIcon />}
                onClick={onCancel}>
                {t("create-new-entity.back-to-page")}
              </Button>
            )}
          {activeStep === CreateNewEntityStep.REVIEW_ENTITY && (
            <Button
              id={"step-action-create-button"}
              data-testid={"step-action-create-button"}
              className={"step-action-button"}
              buttonStyle={"primary"}
              endIcon={<ChevronRightIcon />}
              onClick={createNewEntity}
              isLoading={working}>
              {t("create-new-entity.stepper.controls.actions.create")}
            </Button>
          )}
          {((activeStep === CreateNewEntityStep.RESULTS && stepError) ||
            (activeStep === CreateNewEntityStep.RESULTS && working)) && (
            <Button
              id={"step-action-create-button"}
              data-testid={"step-action-create-button"}
              className={"step-action-button"}
              buttonStyle={"primary"}
              onClick={createNewEntity}
              isLoading={working}>
              {t("create-new-entity.stepper.controls.actions.create")}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};
